import classnames from 'classnames';
import { type NextRouter, useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { useIsMobileView } from '../../hooks/useIsMobileView';

type ExtendedParsedUrlQuery = NextRouter['query'] & { tab: string | undefined };

export type TabPanel = {
  tabLabel: string;
  tabId: string;
  content: React.ReactNode;
};

type TabPanelsProps = {
  panelColumnMinWidth?: number;
  panels: TabPanel[];
};

type TabPanelsViewProps = TabPanelsProps & {
  setActivePanel: (panel: TabPanel | null) => void;
  activePanel: TabPanel;
};

const TabPanels: React.FC<TabPanelsProps> = React.memo((props) => {
  const { panels } = props;
  const { isMobileView, windowWidth } = useIsMobileView();

  const router = useRouter();
  const activePanelId = useMemo(() => (router.query?.tab as string) || null, [router.query]);
  const setActivePanel = useCallback(
    (panel: TabPanel | null) => {
      (router.query as ExtendedParsedUrlQuery).tab = panel?.tabId || undefined;
      router.push(router, undefined, { shallow: true });
    },
    [router],
  );
  const activePanel = useMemo(() => {
    const panelFromURL = panels.find((panel) => panel.tabId === String(activePanelId));
    return panelFromURL || panels[0];
  }, [panels, activePanelId]);

  return (
    <div>
      {isMobileView && <MobileTabPanels {...props} activePanel={activePanel} setActivePanel={setActivePanel} windowWidth={windowWidth} />}
      {!isMobileView && <DesktopTabPanels {...props} activePanel={activePanel} setActivePanel={setActivePanel} />}
    </div>
  );
});
TabPanels.displayName = 'TabPanels';
export default TabPanels;

/************************************************************************************************
 * DesktopTagPanels
 ************************************************************************************************/

const DesktopTabPanels: React.FC<TabPanelsViewProps> = React.memo((props) => {
  const { panels, panelColumnMinWidth, setActivePanel, activePanel } = props;
  return (
    <div className="flex gap-14 my-3">
      <ul role="tablist" className="flex flex-col w-auto" style={{ minWidth: panelColumnMinWidth }} aria-label="Tab List">
        {panels.map((panel, index) => (
          <PanelControlButtonButton
            key={`tab-${index}`}
            label={panel.tabLabel}
            onClick={() => setActivePanel(panel)}
            isSelected={panel === activePanel}
          />
        ))}
      </ul>
      <div className="w-full mt-4" id="tab-panel" role="tabpanel" tabIndex={0}>
        {activePanel?.content}
      </div>
    </div>
  );
});
DesktopTabPanels.displayName = 'DesktopTabPanels';

/************************************************************************************************
 * MobileTabPanels
 ************************************************************************************************/
function calculateOffsetForActivePanel(children: HTMLCollection, activePanelIndex: number, windowWidth: number) {
  let offset = 0;
  // Go through the listRef children and calculate the total width of the panels.
  for (let i = 0; i <= activePanelIndex; i++) {
    const panel = children[i];
    if (panel) {
      offset += panel.clientWidth;
    }
  }
  // Try to center the active panel
  offset -= windowWidth / 2;
  return offset;
}

const MobileTabPanels: React.FC<TabPanelsViewProps & { windowWidth: number }> = React.memo((props) => {
  const { panels, panelColumnMinWidth, windowWidth, setActivePanel, activePanel } = props;
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  // The scroll offset should be calculcated based on the active panel
  React.useEffect(() => {
    const activePanelIndex = panels.findIndex((panel) => panel === activePanel);
    if (listRef.current?.children) {
      const offset = calculateOffsetForActivePanel(listRef.current.children, activePanelIndex, windowWidth);
      // Apply the offset to scrollRef as scrollLeft
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ left: offset, behavior: 'smooth' });
      }
    }
  }, [activePanel, panels, windowWidth]);

  return (
    <div className="overflow-x-auto">
      <div ref={scrollRef} className="w-full overflow-x-scroll">
        <ul
          ref={listRef}
          role="tablist"
          className={classnames('flex flex-row gap-2 items-center my-3 relative')}
          style={{ minWidth: panelColumnMinWidth }}
          aria-label="Tab List"
        >
          {panels.map((panel, index) => (
            <PanelControlButtonButton
              key={`tab-${index}`}
              label={panel.tabLabel}
              onClick={() => setActivePanel(panel)}
              isSelected={panel === activePanel}
              className="min-w-[160px] "
            />
          ))}
        </ul>
      </div>
      <div className="w-full mt-4" id="tab-panel" role="tabpanel" tabIndex={0}>
        {activePanel?.content}
      </div>
    </div>
  );
});
MobileTabPanels.displayName = 'MobileTabPanels';

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

type PanelControlButtonButtonProps = {
  isSelected: boolean;
  label: string;
  className?: string;
  onClick: () => void;
};
const PanelControlButtonButton: React.FC<PanelControlButtonButtonProps> = React.memo((props) => {
  const { label, isSelected, className, onClick } = props;
  return (
    <li className="md:not-last:border-b-1 md:border-gray">
      <button
        className={classnames(
          'block w-full py-4 text-center md:text-left text-xl md:text-2xl font-semibold',
          {
            'text-darkGray': !isSelected,
            'text-purple': isSelected,
          },
          className,
        )}
        role="tab"
        aria-selected={isSelected}
        aria-controls="tab-panel"
        onClick={onClick}
        tabIndex={0}
      >
        {label}
      </button>
    </li>
  );
});
PanelControlButtonButton.displayName = 'PanelControlButtonButton';
